/**
 * MemberProgressService - Aggregates member quest progress data for CRM
 */

import { supabase } from '@/integrations/supabase/client';
import { MemberNotesService } from './member-notes.service';
import { memberActionLogService } from './member-action-log.service';
import { deriveAtRiskReasons } from '@/utils/at-risk-rules';
import { getCurrentLeague, LEAGUE_ORDER } from '@/utils/league-utils';
import type {
  MemberProgressSummary,
  MemberProgressDetail,
  CategoryProgress,
  QuestBlocker,
  RecommendedQuest,
  CompletedQuest,
  RoundSummary,
  ProgressFilters,
  ProgressStats,
  MemberTier,
  MemberStatus,
  HandicapTrend
} from '@/types/crm.types';

// Category mapping for display - Skill-based categories for better progression tracking
const CATEGORY_CONFIG: Record<string, { icon: string; displayName: string }> = {
  // Current skill categories (Trophy Room system)
  'putting':    { icon: '⛳', displayName: 'Putting' },
  'short_game': { icon: '🏌️', displayName: 'Short Game' },
  'bunker':     { icon: '⛏️', displayName: 'Bunker' },
  'precision':  { icon: '⚡', displayName: 'Long Game' },
  'scoring':    { icon: '📈', displayName: 'Scoring' },
  'mental':     { icon: '🧠', displayName: 'Mental Game' },
  // Legacy / outcome categories
  'rounds':     { icon: '🏌️', displayName: 'Rounds' },
  'social':     { icon: '👥', displayName: 'Social' },
  'special':    { icon: '⭐', displayName: 'Special' },
  // Legacy backwards-compat
  'recovery':     { icon: '🏆', displayName: 'Recovery' },
  'consistency':  { icon: '👑', displayName: 'Consistency' },
};


class MemberProgressServiceClass {
  /**
   * Get all club members with their quest progress summary
   */
  async getMembersProgress(
    clubId: string,
    filters?: ProgressFilters
  ): Promise<{ members: MemberProgressSummary[]; stats: ProgressStats }> {
    // Get club members
    const { data: membersData, error: membersError } = await supabase
      .from('user_club_memberships')
      .select(`
        user_id,
        joined_at,
        profiles!inner (
          first_name,
          last_name,
          email,
          avatar_url,
          handicap
        )
      `)
      .eq('club_id', clubId)
      .eq('status', 'active');

    if (membersError) {
      console.error('[MemberProgressService] Error fetching members:', membersError);
      throw membersError;
    }

    if (!membersData || membersData.length === 0) {
      return {
        members: [],
        stats: {
          total_members: 0,
          avg_progress: 0,
          need_attention: 0,
          quests_completed_this_week: 0,
          new_members_this_month: 0,
          onboarding_count: 0
        }
      };
    }

    // Get user IDs
    const userIds = membersData.map(m => m.user_id);

    // Fetch all quest completions — authoritative source for XP and progress counts
    const { data: completionsData } = await supabase
      .from('user_quest_completions')
      .select('user_id, quest_id, completed_at, xp_earned')
      .in('user_id', userIds);

    // Fetch quest definitions for this club: global defaults (club_id IS NULL) + club overrides
    // Also fetch historical (inactive/deleted) rows so old quest IDs still resolve to a league.
    const [{ data: questMetaRows }, { data: historicalMetaRows }] = await Promise.all([
      supabase
        .from('quest_achievements')
        .select('id, league, sort_order, club_id')
        .eq('is_active', true)
        .is('deleted_at', null)
        .or(`club_id.is.null,club_id.eq.${clubId}`),
      supabase
        .from('quest_achievements')
        .select('id, league')
        .or(`club_id.is.null,club_id.eq.${clubId}`),
    ]);

    // Build quest_id → league map: active rows first, then fill gaps with historical rows
    const questLeagueMap = new Map<string, string>(); // all quest IDs → league
    for (const row of (historicalMetaRows ?? [])) {
      questLeagueMap.set(row.id, row.league);
    }
    // Active rows overwrite — ensures deduped club overrides take precedence
    const questsPerLeague = new Map<string, number>(); // league → effective quest count for this club
    const seenByLeagueAndOrder = new Map<string, boolean>(); // dedup key: `${league}:${sort_order}`
    for (const row of (questMetaRows ?? []).sort((a, b) =>
      // Process club rows first so they win over globals in dedup
      (a.club_id === clubId ? 0 : 1) - (b.club_id === clubId ? 0 : 1)
    )) {
      questLeagueMap.set(row.id, row.league);
      const dedupKey = `${row.league}:${row.sort_order}`;
      if (!seenByLeagueAndOrder.has(dedupKey)) {
        seenByLeagueAndOrder.set(dedupKey, true);
        questsPerLeague.set(row.league, (questsPerLeague.get(row.league) ?? 0) + 1);
      }
    }

    // Group completions by user, enriched with resolved league
    const completionsByUser = new Map<string, any[]>();
    for (const row of (completionsData ?? []) as any[]) {
      const uid = row.user_id;
      const league = questLeagueMap.get(row.quest_id);
      if (!completionsByUser.has(uid)) completionsByUser.set(uid, []);
      completionsByUser.get(uid)!.push({ ...row, league });
    }

    // Legacy coachProfiles kept as empty — coaching assignment now lives in coaching_relationships
    const coachProfiles: Record<string, { id: string; full_name: string; avatar_url: string | null }> = {};
    const onboardingData: any[] = []; // unused below; logic moves inline

    // Get recent rounds for last activity (using solo_rounds - the primary rounds table)
    const { data: roundsData } = await supabase
      .from('solo_rounds')
      .select('user_id, completed_at, date')
      .in('user_id', userIds)
      .eq('status', 'completed')
      .order('completed_at', { ascending: false });

    // Get handicap history for trend (from profiles - handicap is stored there)
    const { data: handicapHistory } = await supabase
      .from('solo_rounds')
      .select('user_id, handicap_used, completed_at')
      .in('user_id', userIds)
      .eq('status', 'completed')
      .not('handicap_used', 'is', null)
      .order('completed_at', { ascending: false })
      .limit(userIds.length * 5); // Get last 5 rounds per user

    // Build member summaries
    const members: MemberProgressSummary[] = membersData.map(member => {
      const profile = member.profiles as any;
      const userId = member.user_id;
      const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown';

      // Derive current league by walking quest completions — canonical model
      const userCompletions: any[] = completionsByUser.get(userId) ?? [];
      const completedPerLeagueForWalk: Record<string, number> = {};
      for (const r of userCompletions) {
        if (r.league) completedPerLeagueForWalk[r.league] = (completedPerLeagueForWalk[r.league] ?? 0) + 1;
      }
      const totalQuestsForWalk: Record<string, number> = Object.fromEntries(questsPerLeague);
      const currentLeague = getCurrentLeague(completedPerLeagueForWalk, totalQuestsForWalk);
      const baseTier = this.mapLeagueToTier(currentLeague) || this.calculateTier(0);
      const tier: MemberTier = baseTier;

      const isOnboarding = currentLeague === 'onboarding' || currentLeague === 'hcp_54';

      const completed = userCompletions.filter(r => r.league === currentLeague).length;
      const total = questsPerLeague.get(currentLeague) ?? 0;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      // Per-league completed counts — cap each league to avoid double-counting club overrides
      const completedPerLeague = new Map<string, number>();
      for (const r of userCompletions) {
        if (r.league) completedPerLeague.set(r.league, (completedPerLeague.get(r.league) ?? 0) + 1);
      }
      const clampedPerLeague = (league: string) =>
        Math.min(completedPerLeague.get(league) ?? 0, questsPerLeague.get(league) ?? 0);

      // Overall: sum across all leagues using clamped per-league counts
      const overallTotal = Array.from(questsPerLeague.values()).reduce((s, n) => s + n, 0);
      const overallCompleted = Array.from(questsPerLeague.keys()).reduce((s, l) => s + clampedPerLeague(l), 0);
      const overallPercentage = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0;

      // Onboarding-phase: onboarding + hcp_54 only
      const onboardingPhaseTotal = (questsPerLeague.get('onboarding') ?? 0) + (questsPerLeague.get('hcp_54') ?? 0);
      const onboardingPhaseCompleted = clampedPerLeague('onboarding') + clampedPerLeague('hcp_54');
      const onboardingPhasePercentage = onboardingPhaseTotal > 0 ? Math.round((onboardingPhaseCompleted / onboardingPhaseTotal) * 100) : 0;

      // Get last activity
      const userRounds = (roundsData || []).filter(r => r.user_id === userId);
      const lastRound = userRounds[0];
      const lastActive = lastRound?.completed_at || member.joined_at;

      // Calculate status based on last activity
      const status = this.calculateStatus(lastActive);

      // Calculate handicap trend
      const userHandicaps = (handicapHistory || [])
        .filter(h => h.user_id === userId)
        .slice(0, 5);
      const handicapTrend = this.calculateHandicapTrend(userHandicaps);

      const blockerCount = 0;

      // Build onboarding_data from quest progress if member is in onboarding phase
      let onboarding_data = undefined;
      if (isOnboarding) {
        const stageNumber = Math.min(onboardingPhaseCompleted + 1, Math.max(onboardingPhaseTotal, 1));
        const groupCategory: 'discover' | 'develop' | 'perform' =
          stageNumber <= 3 ? 'discover' : stageNumber <= 6 ? 'develop' : 'perform';

        onboarding_data = {
          current_stage_number: stageNumber,
          current_stage_name: '',
          group_category: groupCategory,
          stage_progress_percentage: 0,
          total_progress_percentage: onboardingPhasePercentage,
          exercises_completed: 0,
          exercises_remaining: 0,
          days_since_last_advance: 0,
          assigned_coach: null
        };
      }

      return {
        member_id: userId,
        profile: {
          full_name: fullName,
          email: profile.email || '',
          avatar_url: profile.avatar_url
        },
        quest_progress: {
          total,
          completed,
          percentage
        },
        overall_progress: {
          total: overallTotal,
          completed: overallCompleted,
          percentage: overallPercentage
        },
        onboarding_progress: {
          total: onboardingPhaseTotal,
          completed: onboardingPhaseCompleted,
          percentage: onboardingPhasePercentage
        },
        tier,
        handicap: profile.handicap,
        handicap_trend: handicapTrend,
        last_active: lastActive,
        status,
        blocker_count: blockerCount,
        member_since: member.joined_at,
        onboarding_data
      };
    });

    // Apply filters
    let filteredMembers = members;

    if (filters?.tier) {
      filteredMembers = filteredMembers.filter(m => m.tier === filters.tier);
    }

    if (filters?.tier_min) {
      const TIER_ORDER = ['onboarding', 'hcp_54', 'hcp_42', 'hcp_36', 'hcp_28', 'hcp_18', 'hcp_10'];
      const minIdx = TIER_ORDER.indexOf(filters.tier_min);
      filteredMembers = filteredMembers.filter(m => TIER_ORDER.indexOf(m.tier) >= minIdx);
    }

    if (filters?.status) {
      filteredMembers = filteredMembers.filter(m => m.status === filters.status);
    }

    if (filters?.stage_group) {
      filteredMembers = filteredMembers.filter(m =>
        m.onboarding_data?.group_category === filters.stage_group
      );
    }

    if (filters?.stage_number !== undefined) {
      filteredMembers = filteredMembers.filter(m =>
        m.onboarding_data?.current_stage_number === filters.stage_number
      );
    }

    if (filters?.stuck) {
      filteredMembers = filteredMembers.filter(m => {
        if (!m.onboarding_data) return false;
        return m.onboarding_data.days_since_last_advance >= 7;
      });
    }

    if (filters?.ready_to_promote) {
      filteredMembers = filteredMembers.filter(m => {
        // Onboarding members: 90%+ progress in onboarding
        if (m.tier === 'onboarding' && m.onboarding_data) {
          return m.onboarding_data.total_progress_percentage >= 90;
        }
        // Graduated members: 90%+ quest completion
        return m.quest_progress.percentage >= 90;
      });
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filteredMembers = filteredMembers.filter(m =>
        m.profile.full_name.toLowerCase().includes(search) ||
        m.profile.email.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    if (filters?.sort_by) {
      filteredMembers.sort((a, b) => {
        const order = filters.sort_order === 'asc' ? 1 : -1;
        switch (filters.sort_by) {
          case 'progress':
            return (a.quest_progress.percentage - b.quest_progress.percentage) * order;
          case 'last_active':
            return (new Date(a.last_active || 0).getTime() - new Date(b.last_active || 0).getTime()) * order;
          case 'name':
            return a.profile.full_name.localeCompare(b.profile.full_name) * order;
          case 'handicap':
            return ((a.handicap || 99) - (b.handicap || 99)) * order;
          default:
            return 0;
        }
      });
    }

    // Calculate stats
    const stats: ProgressStats = {
      total_members: members.length,
      avg_progress: members.length > 0
        ? Math.round(members.reduce((sum, m) => sum + m.quest_progress.percentage, 0) / members.length)
        : 0,
      need_attention: members.filter(m => m.status === 'at_risk').length,
      ready_to_promote: members.filter(m => {
        // Onboarding members: 90%+ progress in onboarding
        if (m.tier === 'onboarding' && m.onboarding_data) {
          return m.onboarding_data.total_progress_percentage >= 90;
        }
        // Graduated members: 90%+ quest completion
        return m.quest_progress.percentage >= 90;
      }).length,
      new_members_this_month: members.filter(m => {
        const memberDate = new Date(m.member_since);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return memberDate > monthAgo;
      }).length,
      onboarding_count: members.filter(m => m.tier === 'onboarding').length
    };

    // Enrich with last_action_at, at_risk_reasons, and snooze state
    const memberIds = filteredMembers.map(m => m.member_id);
    const [lastActionDates, snoozeRecords] = await Promise.all([
      memberActionLogService.getLastActionDates(clubId, memberIds),
      memberActionLogService.getSnoozeState(clubId, memberIds),
    ]);
    const now = Date.now();
    filteredMembers = filteredMembers.map(m => {
      const rec = snoozeRecords[m.member_id];
      let snooze: MemberProgressSummary['snooze'] = null;
      if (rec) {
        const snoozedAtMs = new Date(rec.snoozedAt).getTime();
        const snoozeUntilMs = new Date(rec.snoozeUntil).getTime();
        const lastActiveMs = m.last_active ? new Date(m.last_active).getTime() : 0;
        const hadActivitySinceSnooze = lastActiveMs > snoozedAtMs;
        if (!hadActivitySinceSnooze) {
          snooze = {
            snoozedAt: rec.snoozedAt,
            snoozeUntil: rec.snoozeUntil,
            durationDays: rec.durationDays,
            isActive: snoozeUntilMs > now,
            isExpired: snoozeUntilMs <= now,
          };
        }
      }
      return {
        ...m,
        last_action_at: lastActionDates[m.member_id] ?? null,
        at_risk_reasons: m.status === 'at_risk' ? deriveAtRiskReasons(m) : undefined,
        snooze,
      };
    });

    return { members: filteredMembers, stats };
  }

  /**
   * Get detailed progress for a single member
   */
  async getMemberDetail(clubId: string, memberId: string): Promise<MemberProgressDetail> {
    // Get member profile
    const { data: memberData, error: memberError } = await supabase
      .from('user_club_memberships')
      .select(`
        user_id,
        joined_at,
        profiles!inner (
          first_name,
          last_name,
          email,
          avatar_url,
          handicap
        )
      `)
      .eq('club_id', clubId)
      .eq('user_id', memberId)
      .single();

    if (memberError || !memberData) {
      throw new Error('Member not found');
    }

    const profile = memberData.profiles as any;
    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown';

    // Fetch club quests (for totals) + global quests (for ID resolution of completions)
    const [{ data: allQuests }, { data: allHistoricalQuests }, { data: completionsData }] = await Promise.all([
      supabase
        .from('quest_achievements')
        .select('*')
        .eq('is_active', true)
        .is('deleted_at', null)
        .or(`club_id.eq.${clubId},club_id.is.null`),
      // Historical quests (no active/deleted filter) — needed to resolve old quest IDs to slots
      supabase
        .from('quest_achievements')
        .select('id, league, sort_order')
        .or(`club_id.eq.${clubId},club_id.is.null`),
      // Completions with full fields (quest_id, completed_at, xp_earned)
      supabase
        .from('user_quest_completions')
        .select('*')
        .eq('user_id', memberId),
    ]);
    const memberQuestRows = completionsData;

    // Get user's progress summary
    const { data: progressSummary } = await supabase
      .from('user_quest_progress_summary')
      .select('*')
      .eq('user_id', memberId)
      .maybeSingle();

    // Get user's rounds from all sources: solo, multiplayer, and tournament
    // Fetch solo rounds (no limit - get all rounds)
    const { data: soloRoundsData } = await supabase
      .from('solo_rounds')
      .select('id, completed_at, date, total_score, total_par, par_differential, handicap_used, course_name, course_id, holes_played')
      .eq('user_id', memberId)
      .eq('status', 'completed')
      .order('completed_at', { ascending: false });

    // Fetch multiplayer games - query through game_participants first
    const { data: participantData } = await supabase
      .from('game_participants')
      .select(`
        game_id,
        total_score,
        handicap,
        multiplayer_games!inner (
          id,
          game_date,
          completed_at,
          course_id,
          course_name,
          status
        )
      `)
      .eq('user_id', memberId);
    
    // Filter for completed games and extract game data
    const multiplayerGamesData = (participantData || [])
      .filter(p => p.multiplayer_games && p.multiplayer_games.status === 'completed')
      .map(p => ({
        ...p.multiplayer_games,
        participant: {
          total_score: p.total_score,
          handicap: p.handicap
        }
      }));

    // Fetch tournament rounds from round_details_cache (tournament rounds are cached here)
    const { data: tournamentRoundsData } = await supabase
      .from('round_details_cache')
      .select('*')
      .eq('user_id', memberId)
      .eq('round_type', 'tournament')
      .order('date', { ascending: false });

    // Fetch cache data for all round types to extract stats
    const { data: allCacheData } = await supabase
      .from('round_details_cache')
      .select('*')
      .eq('user_id', memberId)
      .order('date', { ascending: false });

    // Create cache lookup maps by round_id and round_type
    const cacheByRoundId = new Map<string, any>();
    (allCacheData || []).forEach(cache => {
      cacheByRoundId.set(`${cache.round_type}:${cache.round_id}`, cache);
    });

    // Combine all rounds for sorting and use solo rounds for backward compatibility
    const roundsData = soloRoundsData || [];

    // Get notes
    const notes = await MemberNotesService.getNotesByMember(clubId, memberId);

    // Build slot → [all quest IDs] map using ALL historical quests (including inactive/deleted)
    // so completions against old quest IDs still resolve to the correct slot.
    const slotToAllIds = new Map<string, string[]>();
    for (const q of ((allHistoricalQuests ?? []) as any[])) {
      const slot = `${q.league}:${q.sort_order}`;
      if (!slotToAllIds.has(slot)) slotToAllIds.set(slot, []);
      slotToAllIds.get(slot)!.push(q.id);
    }

    // Build active (deduplicated) slots per league from the currently active curriculum.
    // Club-specific rows shadow global defaults at the same slot.
    const detailSeenBySlot = new Map<string, string>(); // slot → canonical league
    const detailQuestsPerLeague = new Map<string, number>();
    for (const q of ((allQuests ?? []) as any[]).sort((a, b) =>
      (a.club_id === clubId ? 0 : 1) - (b.club_id === clubId ? 0 : 1)
    )) {
      const key = `${q.league}:${q.sort_order}`;
      if (!detailSeenBySlot.has(key)) {
        detailSeenBySlot.set(key, q.league);
        detailQuestsPerLeague.set(q.league, (detailQuestsPerLeague.get(q.league) ?? 0) + 1);
      }
    }

    // Build set of quest IDs the member has completed
    const memberCompletedIds = new Set<string>(
      (memberQuestRows ?? []).filter((r: any) => r.completed_at).map((r: any) => r.quest_id)
    );

    // Count completed slots per league (slot-based dedup — same logic as getJourneyProgressForMember)
    const completedByLeague = new Map<string, number>();
    for (const [slot, league] of detailSeenBySlot) {
      const allIdsForSlot = slotToAllIds.get(slot) ?? [];
      if (allIdsForSlot.some(id => memberCompletedIds.has(id))) {
        completedByLeague.set(league, (completedByLeague.get(league) ?? 0) + 1);
      }
    }

    // Derive current league by walking quest completions — canonical model
    const completedForWalk: Record<string, number> = Object.fromEntries(completedByLeague);
    const totalsForWalk: Record<string, number> = Object.fromEntries(detailQuestsPerLeague);
    const currentLeague = getCurrentLeague(completedForWalk, totalsForWalk);
    const baseTierForDetail = this.mapLeagueToTier(currentLeague) || this.calculateTier(0);
    const tier: MemberTier = baseTierForDetail;

    // Onboarding phase spans both 'onboarding' + 'hcp_54' leagues combined.
    const inOnboardingPhase = currentLeague === 'onboarding' || currentLeague === 'hcp_54';
    const leaguesToCount = inOnboardingPhase ? ['onboarding', 'hcp_54'] : [currentLeague];
    const completed = leaguesToCount.reduce((sum, l) => sum + (completedByLeague.get(l) ?? 0), 0);
    const total = leaguesToCount.reduce((sum, l) => sum + (detailQuestsPerLeague.get(l) ?? 0), 0);
    const isOnboarding = currentLeague === 'onboarding';
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Per-league breakdown — single source of truth for all progress counts
    const perLeagueProgress = LEAGUE_ORDER
      .filter(l => detailQuestsPerLeague.has(l))
      .map(l => {
        const leagueTotal = detailQuestsPerLeague.get(l) ?? 0;
        const leagueCompleted = completedByLeague.get(l) ?? 0;
        return {
          league: l,
          completed: leagueCompleted,
          total: leagueTotal,
          percentage: leagueTotal > 0 ? Math.round((leagueCompleted / leagueTotal) * 100) : 0,
        };
      });

    // Derive overall and onboarding totals by summing per-league (consistent with Performance tab)
    const detailOverallCompleted = perLeagueProgress.reduce((s, l) => s + l.completed, 0);
    const detailOverallTotal = perLeagueProgress.reduce((s, l) => s + l.total, 0);
    const detailOverallPercentage = detailOverallTotal > 0 ? Math.round((detailOverallCompleted / detailOverallTotal) * 100) : 0;

    const onboardingLeagues = perLeagueProgress.filter(l => l.league === 'onboarding' || l.league === 'hcp_54');
    const detailOnboardingCompleted = onboardingLeagues.reduce((s, l) => s + l.completed, 0);
    const detailOnboardingTotal = onboardingLeagues.reduce((s, l) => s + l.total, 0);
    const detailOnboardingPercentage = detailOnboardingTotal > 0 ? Math.round((detailOnboardingCompleted / detailOnboardingTotal) * 100) : 0;

    // Build round summaries from all sources (solo, multiplayer, tournament)
    const recentRounds = this.buildRoundSummariesFromAllSources(
      soloRoundsData || [],
      multiplayerGamesData || [],
      tournamentRoundsData || [],
      cacheByRoundId
    );

    // Get last activity from all rounds (sorted by date)
    const allRoundsSorted = recentRounds.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const lastRound = allRoundsSorted[0];
    const lastActive = lastRound?.date || memberData.joined_at;
    const status = this.calculateStatus(lastActive);

    // Calculate handicap trend from solo rounds (for compatibility)
    const handicapTrend = this.calculateHandicapTrendFromSoloRounds(soloRoundsData || []);

    // Build category progress (using completionsData - completed quests only)
    const progressByCategory = this.buildCategoryProgressFromCompletions(allQuests || [], completionsData || []);

    // Blockers not available with completion-only tracking
    const blockers: QuestBlocker[] = [];

    // Build recommended quests (quests not yet completed)
    const nextRecommended = this.buildRecommendedQuestsFromCompletions(allQuests || [], completionsData || []);

    // Build recent achievements
    const recentAchievements = this.buildRecentAchievementsFromCompletions(completionsData || [], allQuests || []);

    return {
      member_id: memberId,
      profile: {
        full_name: fullName,
        email: profile.email || '',
        avatar_url: profile.avatar_url
      },
      quest_progress: {
        total,
        completed,
        percentage
      },
      overall_progress: {
        total: detailOverallTotal,
        completed: detailOverallCompleted,
        percentage: detailOverallPercentage
      },
      onboarding_progress: {
        total: detailOnboardingTotal,
        completed: detailOnboardingCompleted,
        percentage: detailOnboardingPercentage
      },
      tier,
      handicap: profile.handicap,
      handicap_trend: handicapTrend,
      last_active: lastActive,
      status,
      blocker_count: blockers.length,
      member_since: memberData.joined_at,
      progress_by_category: progressByCategory,
      per_league_progress: perLeagueProgress,
      blockers,
      next_recommended: nextRecommended,
      recent_achievements: recentAchievements,
      recent_rounds: recentRounds,
      notes,
      total_rounds: recentRounds.length
    };
  }

  // ============================================
  // Private Helper Methods
  // ============================================

  private calculateTier(completedQuests: number): MemberTier {
    if (completedQuests >= 30) return 'hcp_10';
    if (completedQuests >= 20) return 'hcp_18';
    if (completedQuests >= 12) return 'hcp_28';
    if (completedQuests >= 8) return 'hcp_36';
    if (completedQuests >= 4) return 'hcp_42';
    if (completedQuests >= 1) return 'hcp_54';
    return 'hcp_54';
  }

  private mapLeagueToTier(league: string | undefined): MemberTier | null {
    if (!league) return null;
    const leagueLower = league.toLowerCase();
    if (leagueLower === 'hcp_10') return 'hcp_10';
    if (leagueLower === 'hcp_18') return 'hcp_18';
    if (leagueLower === 'hcp_28') return 'hcp_28';
    if (leagueLower === 'hcp_36') return 'hcp_36';
    if (leagueLower === 'hcp_42') return 'hcp_42';
    if (leagueLower === 'hcp_54') return 'hcp_54';
    if (leagueLower === 'onboarding') return 'onboarding';
    return null;
  }

  private calculateStatus(lastActive: string | null): MemberStatus {
    if (!lastActive) return 'at_risk';

    const daysSinceActive = Math.floor(
      (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceActive <= 7) return 'active';
    if (daysSinceActive <= 14) return 'slowing';
    return 'at_risk';
  }

  private calculateHandicapTrend(handicaps: { handicap_used?: number | null }[]): HandicapTrend {
    const validHandicaps = handicaps.filter(h => h.handicap_used != null);
    if (validHandicaps.length < 2) return 'stable';

    const recent = validHandicaps[0].handicap_used!;
    const older = validHandicaps[validHandicaps.length - 1].handicap_used!;
    const diff = recent - older;

    if (diff < -0.5) return 'improving';
    if (diff > 0.5) return 'declining';
    return 'stable';
  }

  private calculateHandicapTrendFromRounds(rounds: any[]): HandicapTrend {
    const withHandicap = rounds.filter(r => r.handicap_at_time != null);
    if (withHandicap.length < 2) return 'stable';

    const recent = withHandicap[0].handicap_at_time;
    const older = withHandicap[withHandicap.length - 1].handicap_at_time;
    const diff = recent - older;

    if (diff < -0.5) return 'improving';
    if (diff > 0.5) return 'declining';
    return 'stable';
  }

  private calculateHandicapTrendFromSoloRounds(rounds: any[]): HandicapTrend {
    const withHandicap = rounds.filter(r => r.handicap_used != null);
    if (withHandicap.length < 2) return 'stable';

    const recent = withHandicap[0].handicap_used;
    const older = withHandicap[withHandicap.length - 1].handicap_used;
    const diff = recent - older;

    if (diff < -0.5) return 'improving';
    if (diff > 0.5) return 'declining';
    return 'stable';
  }

  private countBlockers(progress: any[]): number {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return progress.filter(p => {
      if (p.completed_at) return false;
      const updated = new Date(p.updated_at || p.created_at);
      return updated < sevenDaysAgo;
    }).length;
  }

  private countRecentCompletions(progress: any[], days: number): number {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    return progress.filter(p => {
      if (!p.completed_at) return false;
      return new Date(p.completed_at) > cutoff;
    }).length;
  }

  private buildCategoryProgressFromCompletions(allQuests: any[], completions: any[]): CategoryProgress[] {
    const completedQuestIds = new Set(completions.map(c => c.quest_id));
    const categories = new Map<string, { total: number; completed: number; quests: any[] }>();

    // Group quests by category
    allQuests.forEach(quest => {
      const cat = quest.category || 'other';
      if (!categories.has(cat)) {
        categories.set(cat, { total: 0, completed: 0, quests: [] });
      }
      const catData = categories.get(cat)!;
      catData.total++;
      catData.quests.push(quest);

      if (completedQuestIds.has(quest.id)) {
        catData.completed++;
      }
    });

    // Find weakest category
    let weakestPercentage = 100;
    let weakestCategory = '';
    categories.forEach((data, cat) => {
      const pct = data.total > 0 ? (data.completed / data.total) * 100 : 0;
      if (pct < weakestPercentage) {
        weakestPercentage = pct;
        weakestCategory = cat;
      }
    });

    // Build result
    const result: CategoryProgress[] = [];
    categories.forEach((data, cat) => {
      const config = CATEGORY_CONFIG[cat] || { icon: '📋', displayName: cat };
      const percentage = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

      // Find next incomplete quest
      const incompleteQuest = data.quests.find(q => !completedQuestIds.has(q.id));

      result.push({
        category: cat,
        category_icon: config.icon,
        total: data.total,
        completed: data.completed,
        percentage,
        next_quest: incompleteQuest ? {
          id: incompleteQuest.id,
          title: incompleteQuest.name
        } : null,
        is_weakest: cat === weakestCategory
      });
    });

    // Sort by percentage ascending (weakest first)
    return result.sort((a, b) => a.percentage - b.percentage);
  }

  // Legacy method - kept for compatibility
  private buildCategoryProgress(allQuests: any[], progress: any[]): CategoryProgress[] {
    return this.buildCategoryProgressFromCompletions(allQuests, progress);
  }

  private buildBlockers(progress: any[], allQuests: any[]): QuestBlocker[] {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return progress
      .filter(p => {
        if (p.completed_at) return false;
        const updated = new Date(p.updated_at || p.created_at);
        return updated < sevenDaysAgo;
      })
      .map(p => {
        const quest = p.quest || allQuests.find(q => q.id === p.quest_id);
        const updated = new Date(p.updated_at || p.created_at);
        const daysStuck = Math.floor((Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24));

        return {
          quest_id: p.quest_id,
          title: quest?.name || 'Unknown Quest',
          category: quest?.category || 'other',
          started_at: p.created_at,
          days_stuck: daysStuck,
          current_progress: p.progress?.current || 0,
          target: p.progress?.target || 1
        };
      })
      .sort((a, b) => b.days_stuck - a.days_stuck);
  }

  private buildRecommendedQuestsFromCompletions(allQuests: any[], completions: any[]): RecommendedQuest[] {
    const completedQuestIds = new Set(completions.map(c => c.quest_id));

    // Get quests not yet completed, prioritize by difficulty (easier first)
    return allQuests
      .filter(q => !completedQuestIds.has(q.id))
      .slice(0, 5)
      .map(quest => ({
        id: quest.id,
        title: quest.name || 'Unknown Quest',
        category: quest.category || 'other',
        progress_percentage: 0,
        progress_text: 'Not started',
        description: quest.description || null
      }));
  }

  // Legacy method - kept for compatibility
  private buildRecommendedQuests(progress: any[], allQuests: any[]): RecommendedQuest[] {
    return this.buildRecommendedQuestsFromCompletions(allQuests, progress);
  }

  private buildRecentAchievementsFromCompletions(completions: any[], allQuests: any[]): CompletedQuest[] {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const questMap = new Map(allQuests.map(q => [q.id, q]));

    return completions
      .filter(c => new Date(c.completed_at) > thirtyDaysAgo)
      .map(c => {
        const quest = questMap.get(c.quest_id);
        return {
          id: c.quest_id,
          title: quest?.name || 'Unknown Quest',
          category: quest?.category || 'other',
          completed_at: c.completed_at,
          description: quest?.description || null
        };
      })
      .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
      .slice(0, 5);
  }

  // Legacy method - kept for compatibility
  private buildRecentAchievements(progress: any[]): CompletedQuest[] {
    return this.buildRecentAchievementsFromCompletions(progress, []);
  }

  private buildRoundSummariesFromSoloRounds(rounds: any[]): RoundSummary[] {
    return rounds.map(round => ({
      id: round.id,
      date: round.completed_at || round.date,
      course_name: round.course_name || 'Unknown Course',
      score: round.total_score || 0,
      to_par: round.par_differential ?? 0,
      fir_percentage: null, // Available in solo_scores if needed
      gir_percentage: null,
      total_putts: null,
      handicap_at_time: round.handicap_used,
      round_type: 'solo' as const
    }));
  }

  private buildRoundSummariesFromAllSources(
    soloRounds: any[],
    multiplayerGames: any[],
    tournamentRounds: any[],
    cacheByRoundId?: Map<string, any>
  ): RoundSummary[] {
    const allRounds: RoundSummary[] = [];
    const cacheMap = cacheByRoundId || new Map<string, any>();

    // Process solo rounds - extract stats from cache if available
    const soloSummaries = soloRounds.map(round => {
      const cacheKey = `solo:${round.id}`;
      const cache = cacheMap.get(cacheKey);
      
      // Extract stats from cache (fast access columns or performance_summary)
      let firPercentage: number | null = null;
      let girPercentage: number | null = null;
      let totalPutts: number | null = null;

      if (cache) {
        const perfSummary = cache.complete_data?.performance_summary;
        // Try fast access columns first
        if (cache.fairway_attempts && cache.fairway_attempts > 0 && cache.fairways_hit !== null) {
          firPercentage = Math.round((cache.fairways_hit / cache.fairway_attempts) * 100);
        } else if (perfSummary?.fairways_total && perfSummary.fairways_total > 0) {
          firPercentage = perfSummary.fairways_percentage || 
            Math.round((perfSummary.fairways_hit / perfSummary.fairways_total) * 100);
        }

        if (cache.gir_count !== null && cache.complete_data?.performance_summary?.greens_total) {
          const totalHoles = cache.complete_data.performance_summary.greens_total || 18;
          girPercentage = Math.round((cache.gir_count / totalHoles) * 100);
        } else if (perfSummary?.greens_total && perfSummary.greens_total > 0) {
          girPercentage = perfSummary.greens_percentage || 
            Math.round((perfSummary.greens_hit / perfSummary.greens_total) * 100);
        }

        totalPutts = cache.total_putts || perfSummary?.total_putts || null;
      }

      return {
        id: round.id,
        date: round.completed_at || round.date,
        course_name: cache?.course_name || round.course_name || 'Unknown Course',
        score: cache?.total_score || round.total_score || 0,
        // round.par_differential is the ground truth written at completion.
        // cache.score_to_par was historically written with || 0 which stored 0 for
        // rounds with null par_differential — making every uncached round look like even par.
        // Prefer round.par_differential when available; only fall back to cache if it's null.
        to_par: round.par_differential ?? cache?.score_to_par ?? 0,
        fir_percentage: firPercentage,
        gir_percentage: girPercentage,
        total_putts: totalPutts,
        handicap_at_time: cache?.complete_data?.round?.handicap_used || round.handicap_used || null,
        round_type: 'solo' as const
      };
    });
    allRounds.push(...soloSummaries);

    // Process multiplayer games - extract stats from cache if available
    const multiplayerSummaries = multiplayerGames.map(game => {
      const participant = game.participant;
      const cacheKey = `multiplayer:${game.id}`;
      const cache = cacheMap.get(cacheKey);
      
      // Extract stats from cache
      let firPercentage: number | null = null;
      let girPercentage: number | null = null;
      let totalPutts: number | null = null;

      if (cache) {
        const perfSummary = cache.complete_data?.performance_summary;
        // Try fast access columns first
        if (cache.fairway_attempts && cache.fairway_attempts > 0 && cache.fairways_hit !== null) {
          firPercentage = Math.round((cache.fairways_hit / cache.fairway_attempts) * 100);
        } else if (perfSummary?.fairways_total && perfSummary.fairways_total > 0) {
          firPercentage = perfSummary.fairways_percentage || 
            Math.round((perfSummary.fairways_hit / perfSummary.fairways_total) * 100);
        }

        if (cache.gir_count !== null && cache.complete_data?.performance_summary?.greens_total) {
          const totalHoles = cache.complete_data.performance_summary.greens_total || 18;
          girPercentage = Math.round((cache.gir_count / totalHoles) * 100);
        } else if (perfSummary?.greens_total && perfSummary.greens_total > 0) {
          girPercentage = perfSummary.greens_percentage || 
            Math.round((perfSummary.greens_hit / perfSummary.greens_total) * 100);
        }

        totalPutts = cache.total_putts || perfSummary?.total_putts || null;
      }

      const courseName = cache?.course_name || game.course_name || 'Unknown Course';
      const totalScore = cache?.total_score || participant?.total_score || 0;
      const toPar = cache?.score_to_par ?? 0;

      return {
        id: game.id,
        date: cache?.date || game.completed_at || game.game_date,
        course_name: courseName,
        score: totalScore,
        to_par: toPar,
        fir_percentage: firPercentage,
        gir_percentage: girPercentage,
        total_putts: totalPutts,
        handicap_at_time: cache?.complete_data?.round?.handicap_used || participant?.handicap || null,
        round_type: 'multiplayer' as const
      };
    });
    allRounds.push(...multiplayerSummaries);

    // Process tournament rounds from cache - extract stats from performance_summary
    const tournamentSummaries = tournamentRounds.map(cacheEntry => {
      // Extract data from complete_data JSONB
      const completeData = cacheEntry.complete_data || {};
      const roundData = completeData.round || {};
      const perfSummary = completeData.performance_summary || {};
      
      // Extract stats from cache (fast access columns or performance_summary)
      let firPercentage: number | null = null;
      let girPercentage: number | null = null;
      let totalPutts: number | null = null;

      // Try fast access columns first
      if (cacheEntry.fairway_attempts && cacheEntry.fairway_attempts > 0 && cacheEntry.fairways_hit !== null) {
        firPercentage = Math.round((cacheEntry.fairways_hit / cacheEntry.fairway_attempts) * 100);
      } else if (perfSummary.fairways_total && perfSummary.fairways_total > 0) {
        firPercentage = perfSummary.fairways_percentage || 
          Math.round((perfSummary.fairways_hit / perfSummary.fairways_total) * 100);
      }

      if (cacheEntry.gir_count !== null && perfSummary.greens_total) {
        girPercentage = Math.round((cacheEntry.gir_count / perfSummary.greens_total) * 100);
      } else if (perfSummary.greens_total && perfSummary.greens_total > 0) {
        girPercentage = perfSummary.greens_percentage || 
          Math.round((perfSummary.greens_hit / perfSummary.greens_total) * 100);
      }

      totalPutts = cacheEntry.total_putts || perfSummary.total_putts || null;
      
      // Use cache fields directly (they're already parsed and optimized)
      const courseName = cacheEntry.course_name || roundData.course_name || 'Unknown Course';
      const totalScore = cacheEntry.total_score || roundData.total_strokes || 0;
      const toPar = cacheEntry.score_to_par ?? 0;
      
      // Extract handicap from complete_data if available
      const handicapAtTime = roundData.handicap_used || null;

      return {
        id: cacheEntry.round_id, // Use round_id from cache as the ID
        date: cacheEntry.date, // Date is already in the cache
        course_name: courseName,
        score: totalScore,
        to_par: toPar,
        fir_percentage: firPercentage,
        gir_percentage: girPercentage,
        total_putts: totalPutts,
        handicap_at_time: handicapAtTime,
        round_type: 'tournament' as const
      };
    });
    allRounds.push(...tournamentSummaries);

    // Sort all rounds by date (most recent first)
    return allRounds.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  // Legacy method - kept for compatibility
  private buildRoundSummaries(rounds: any[], courseNames: Record<string, string>): RoundSummary[] {
    return rounds.map(round => {
      const toPar = round.total_score && round.course_par
        ? round.total_score - round.course_par
        : 0;

      return {
        id: round.id,
        date: round.created_at,
        course_name: courseNames[round.course_id] || 'Unknown Course',
        score: round.total_score || 0,
        to_par: toPar,
        fir_percentage: null,
        gir_percentage: null,
        total_putts: null,
        handicap_at_time: round.handicap_at_time
      };
    });
  }
}

export const MemberProgressService = new MemberProgressServiceClass();
